import React from 'react';
import { Paperclip, Mic, Square, ArrowUp } from 'lucide-react';
import RobbieRecordingIndicator from './RobbieRecordingIndicator';

/**
 * v2 Robbie chat input row — shared by the docked and floating
 * panels, ported from the mocks: optional paperclip, "Ask Robbie…" field,
 * ALWAYS a mic (voice is a first-class input), violet send arrow. Presentational
 * — callers own the mic recording lifecycle (see `useVoiceRecorder`) and pass
 * `recording` (+ `recordingLevels`/`recordingElapsedMs`) back down so this row
 * reflects it live; callers may omit handlers to get an honest disabled
 * affordance.
 */
export interface RobbieChatInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSend?: () => void;
  onMic?: () => void;
  /** Mic is actively capturing — swaps the mic button to stop and the field to a live level meter. */
  recording?: boolean;
  /** Real input levels (0..1) while recording — see `useVoiceRecorder`. */
  recordingLevels?: number[];
  /** Elapsed recording time in ms — see `useVoiceRecorder`. */
  recordingElapsedMs?: number;
  /** The recorder's hard cap in ms, so the row can warn as it approaches. */
  recordingMaxMs?: number;
  /** Paperclip attach affordance (docked panel only in the mocks). */
  onAttach?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Only Send (and Enter) wait — the field stays typeable, e.g. while Robbie writes. */
  sendDisabled?: boolean;
  /** Docked panel uses the denser 12px row. */
  dense?: boolean;
  className?: string;
}

export const RobbieChatInput: React.FC<RobbieChatInputProps> = ({
  value,
  onChange,
  onSend,
  onMic,
  recording = false,
  recordingLevels = [],
  recordingElapsedMs = 0,
  recordingMaxMs,
  onAttach,
  placeholder = 'Ask Robbie…',
  disabled = false,
  sendDisabled = false,
  dense = false,
  className = '',
}) => (
  <div
    className={`flex items-center gap-s2 rounded-[9px] border bg-surface py-[5px] pl-s4 pr-[5px] transition-colors duration-150 ${
      recording ? 'border-danger-border' : 'border-robbie-border'
    } ${disabled && !recording ? 'opacity-60' : ''} ${className}`}
  >
    {onAttach && !recording && (
      <button type="button" onClick={onAttach} disabled={disabled} aria-label="Attach a file" className="shrink-0 text-ink-muted hover:text-ink-secondary disabled:pointer-events-none">
        <Paperclip className="h-[13px] w-[13px]" />
      </button>
    )}
    {recording ? (
      <RobbieRecordingIndicator levels={recordingLevels} elapsedMs={recordingElapsedMs} maxDurationMs={recordingMaxMs} />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && !sendDisabled && value?.trim() && onSend?.()}
        placeholder={placeholder}
        disabled={disabled}
        aria-label="Ask Robbie"
        className={`w-full border-none bg-transparent font-sans text-ink outline-none placeholder:text-ink-muted disabled:cursor-not-allowed ${
          dense ? 'text-body-sm' : 'text-body-md'
        }`}
      />
    )}
    <button
      type="button"
      onClick={onMic}
      // Stop is never disabled: whatever else the panel is busy with, a live
      // mic must always be stoppable, or the recording runs to its cap.
      disabled={disabled && !recording}
      aria-label={recording ? 'Stop recording' : 'Start recording'}
      aria-pressed={recording}
      // Recording uses `danger` ink deliberately (see RobbieRecordingIndicator) —
      // not an error, but the near-universal "live" convention beats azure here.
      className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-round transition-colors duration-100
        focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 disabled:pointer-events-none ${
          recording ? 'bg-danger-ink text-white' : 'text-ink-muted hover:bg-line-divider hover:text-ink-secondary'
        }`}
    >
      {recording ? <Square className="h-[10px] w-[10px] fill-current" /> : <Mic className="h-[14px] w-[14px]" />}
    </button>
    {!recording && (
      <button
        type="button"
        onClick={onSend}
        disabled={disabled || sendDisabled || !value?.trim()}
        aria-label="Send"
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-round text-robbie transition-colors
          duration-100 hover:bg-robbie-bg hover:text-robbie-ink focus:outline-none focus-visible:ring-2
          focus-visible:ring-action focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-60"
      >
        <ArrowUp className="h-[14px] w-[14px]" />
      </button>
    )}
  </div>
);

export default RobbieChatInput;
